import { useEffect, useState } from "react";

const MILLI = 1000;

/** 1234567 milli → "1,234.57". Whole numbers drop the decimals. */
export function chips(m: number, opts: { signed?: boolean; decimals?: number } = {}): string {
  const v = m / MILLI;
  const decimals = opts.decimals ?? (Number.isInteger(Math.round(v * 100) / 100) ? 0 : 2);
  const s = Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  const sign = v < 0 ? "−" : opts.signed && v > 0 ? "+" : "";
  return sign + s;
}

/** Chips typed by a user → milli, or null if it isn't a valid amount. */
export function parseChips(input: string): number | null {
  const t = input.trim().replace(/,/g, "");
  if (!/^\d+(\.\d{0,3})?$/.test(t)) return null;
  return Math.round(Number(t) * MILLI);
}

/** 0.6234 → "62%"; below 1% and above 99% keep one decimal. */
export function pct(p: number): string {
  const v = p * 100;
  if (v > 0 && v < 1) return `${v.toFixed(1)}%`;
  if (v > 99 && v < 100) return `${v.toFixed(1)}%`;
  return `${Math.round(v)}%`;
}

const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["day", 86_400_000],
  ["hour", 3_600_000],
  ["minute", 60_000],
];

/** "in 3 hours", "2 days ago", "now". */
export function relative(iso: string | null, now = Date.now()): string {
  if (!iso) return "";
  const diff = Date.parse(iso) - now;
  for (const [unit, ms] of UNITS) {
    if (Math.abs(diff) >= ms) return rtf.format(Math.round(diff / ms), unit);
  }
  return "now";
}

/** Remaining time as "2d 4h", "3h 12m", "4m 09s"; "0s" when past. */
export function countdown(iso: string | null, now = Date.now()): string {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.parse(iso) - now) / 1000));
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

/** Local date-time for display: "Oct 3, 2:30 PM". */
export function when(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/** Value for <input type="datetime-local"> from a Date, in local time. */
export function toLocalInput(d: Date): string {
  const off = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
}

/** <input type="datetime-local"> value → RFC 3339 UTC. */
export function fromLocalInput(v: string): string {
  return new Date(v).toISOString();
}

/** Re-renders every `ms` so countdowns tick. */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

/** Label for an outcome index or "NA". */
export function outcomeLabel(outcomes: string[], choice: number | "NA" | null): string {
  if (choice === null) return "";
  if (choice === "NA" || choice === -1) return "N/A";
  return outcomes[choice] ?? `#${choice}`;
}
